import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import {
  ArrowLeft, Copy, Check, ExternalLink, Building2, Users, Activity as ActivityIcon,
  Mail, MessageCircle, ShoppingCart, PackageCheck, Truck, Receipt, Boxes,
} from "lucide-react";
import { toast } from "sonner";

const BIZ_META = {
  purchase_orders:   { label: "Purchase orders",     icon: ShoppingCart },
  requisitions:      { label: "Requisitions",        icon: ShoppingCart },
  grns:              { label: "GRNs filed",          icon: PackageCheck },
  org_dispatches:    { label: "Dispatches",          icon: Truck },
  sales_invoices:    { label: "Sales invoices",      icon: Receipt },
  vendor_invoices:   { label: "Vendor invoices",     icon: Receipt },
  finished_products: { label: "Finished products",   icon: Boxes },
  items:             { label: "Item masters",        icon: Boxes },
  customers:         { label: "Customers",           icon: Users },
  vendors:           { label: "Vendors",             icon: Building2 },
};

export default function TenantActivity() {
  const { tenantId } = useParams();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get(`/admin/tenants/${tenantId}/activity`).then(({ data }) => setData(data))
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load activity"));
  }, [tenantId]);

  if (!data) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const t = data.tenant;
  const usedPct = data.user_totals?.plan_max
    ? Math.min(100, Math.round((data.user_totals.used / data.user_totals.plan_max) * 100))
    : 0;

  const fullPortalUrl = data.portal_url?.startsWith("http")
    ? data.portal_url
    : `${window.location.origin}${data.portal_url}`;

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(fullPortalUrl);
      setCopied(true); toast.success("Portal URL copied");
      setTimeout(() => setCopied(false), 1600);
    } catch { toast.error("Copy failed"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="tenant-activity">
      <div className="flex items-center gap-3">
        <Link to={`/admin/${data.portal.toLowerCase()}s`}>
          <Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
        </Link>
        <Badge variant="outline" className="font-mono">{data.portal}</Badge>
        <Badge variant="outline" className="font-mono text-[10px]">{t.status}</Badge>
      </div>

      <div>
        <div className="label-overline">{data.portal} activity</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black mt-2 flex items-center gap-3">
          <Building2 className="w-8 h-8 text-orange-500" /> {t.name}
        </h1>
        <p className="text-muted-foreground mt-1 font-mono text-xs">{t.slug} · {t.contact_email}</p>
      </div>

      {/* Portal URL card */}
      <Card data-testid="portal-url-card">
        <CardHeader><CardTitle className="text-sm font-bold flex items-center gap-2"><ExternalLink className="w-4 h-4" /> Customized portal URL</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <code className="text-xs bg-muted px-3 py-2 rounded-md font-mono flex-1 overflow-x-auto" data-testid="portal-url-value">{fullPortalUrl}</code>
            <Button size="sm" variant="outline" onClick={copyUrl} data-testid="copy-portal-url">
              {copied ? <Check className="w-4 h-4 mr-1 text-emerald-600" /> : <Copy className="w-4 h-4 mr-1" />} Copy
            </Button>
            <a href={fullPortalUrl} target="_blank" rel="noreferrer">
              <Button size="sm" data-testid="open-portal-url"><ExternalLink className="w-4 h-4 mr-1" /> Open portal</Button>
            </a>
          </div>
          {data.admin_user ? (
            <p className="text-xs text-muted-foreground">Admin: <span className="font-mono">{data.admin_user.email}</span> · Role <span className="font-mono">{data.admin_user.role}</span></p>
          ) : <p className="text-xs text-amber-700">No admin user attached to this tenant yet.</p>}
        </CardContent>
      </Card>

      {/* Users vs plan max */}
      <Card data-testid="users-plan-card">
        <CardHeader><CardTitle className="text-sm font-bold flex items-center gap-2"><Users className="w-4 h-4" /> Users vs plan limit</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between text-sm">
            <span>{data.user_totals.used} / {data.user_totals.plan_max ?? "—"} seats used</span>
            <span className="font-mono text-muted-foreground">{usedPct}%</span>
          </div>
          <Progress value={usedPct} className={usedPct >= 90 ? "[&>div]:bg-red-500" : usedPct >= 70 ? "[&>div]:bg-amber-500" : ""} />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 pt-3">
            {data.role_counts.map((r) => (
              <div key={r.role} className="flex items-center justify-between px-3 py-2 rounded-lg border border-border" data-testid={`role-count-${r.role}`}>
                <span className="text-xs font-mono">{r.role}</span>
                <Badge variant="outline" className="font-mono text-[10px]">{r.count}</Badge>
              </div>
            ))}
            {data.role_counts.length === 0 && <div className="text-xs text-muted-foreground">No users yet.</div>}
          </div>
        </CardContent>
      </Card>

      {/* Business object counts */}
      <Card data-testid="business-counts-card">
        <CardHeader><CardTitle className="text-sm font-bold flex items-center gap-2"><ActivityIcon className="w-4 h-4" /> How they use the portal</CardTitle></CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {Object.entries(data.business_counts).map(([coll, count]) => {
              const meta = BIZ_META[coll] || { label: coll, icon: Boxes };
              const Icon = meta.icon;
              return (
                <div key={coll} className="p-3 rounded-lg border border-border bg-muted/30" data-testid={`biz-${coll}`}>
                  <Icon className="w-4 h-4 text-muted-foreground mb-2" />
                  <div className="text-xs text-muted-foreground">{meta.label}</div>
                  <div className="font-display text-xl font-black">{count}</div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Action stats table */}
      <Card data-testid="action-stats-card">
        <CardHeader><CardTitle className="text-sm font-bold">Top actions (audit-log)</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow><TableHead>Action</TableHead><TableHead className="text-right">Count</TableHead></TableRow></TableHeader>
            <TableBody>
              {data.action_stats.map((a) => (
                <TableRow key={a.action}>
                  <TableCell className="font-mono text-xs">{a.action}</TableCell>
                  <TableCell className="text-right"><Badge variant="outline">{a.count}</Badge></TableCell>
                </TableRow>
              ))}
              {data.action_stats.length === 0 && <TableRow><TableCell colSpan={2} className="text-center text-muted-foreground py-6">No actions logged.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Recent activity */}
      <Card data-testid="recent-activity-card">
        <CardHeader><CardTitle className="text-sm font-bold">Recent activity</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-1">
            {data.recent_activity.map((r) => (
              <div key={r.id} className="flex items-center justify-between text-xs border-b last:border-b-0 py-2 gap-3">
                <Badge variant="outline" className="font-mono text-[10px]">{r.action}</Badge>
                <span className="text-muted-foreground flex-1 truncate">{r.actor_email} → {r.entity_type} {r.entity_id?.slice(0, 8)}</span>
                <span className="font-mono text-[10px] text-muted-foreground">{new Date(r.created_at).toLocaleString()}</span>
              </div>
            ))}
            {data.recent_activity.length === 0 && <div className="text-center text-muted-foreground py-6">No audit entries yet.</div>}
          </div>
        </CardContent>
      </Card>

      {/* Welcome message */}
      {data.welcome_message ? (
        <Card data-testid="welcome-msg-card">
          <CardHeader><CardTitle className="text-sm font-bold flex items-center gap-2"><Mail className="w-4 h-4" /> Onboarding message · {data.welcome_message.delivery_status}</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-xs">
            <div className="flex flex-wrap gap-4">
              <div><span className="text-muted-foreground">Email to:</span> <code className="font-mono">{data.welcome_message.email_to}</code></div>
              <div><span className="text-muted-foreground">Phone:</span> <code className="font-mono">{data.welcome_message.phone_to || "—"}</code></div>
              <div><span className="text-muted-foreground">Channels:</span> {data.welcome_message.channels.map((c) => <Badge key={c} variant="outline" className="ml-1 text-[10px]">{c === "whatsapp" ? <MessageCircle className="w-3 h-3 mr-0.5 inline text-emerald-600" /> : <Mail className="w-3 h-3 mr-0.5 inline text-blue-600" />}{c}</Badge>)}</div>
            </div>
            <div>
              <div className="text-muted-foreground font-semibold mb-1">Email subject</div>
              <div className="font-mono text-xs bg-muted p-2 rounded">{data.welcome_message.email_subject}</div>
            </div>
            <div>
              <div className="text-muted-foreground font-semibold mb-1">Email body preview</div>
              <pre className="font-mono text-[11px] bg-muted p-3 rounded whitespace-pre-wrap">{data.welcome_message.email_body}</pre>
            </div>
            <div>
              <div className="text-muted-foreground font-semibold mb-1">WhatsApp body</div>
              <pre className="font-mono text-[11px] bg-muted p-3 rounded whitespace-pre-wrap">{data.welcome_message.whatsapp_body}</pre>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card data-testid="welcome-msg-empty">
          <CardContent className="p-4 text-xs text-muted-foreground">No onboarding welcome message on record. It is generated automatically when a subscription is approved.</CardContent>
        </Card>
      )}
    </div>
  );
}
